import { Component, computed, effect, inject, input, NgZone, signal } from '@angular/core';
import { form, FormField } from '@angular/forms/signals';
import { UserWidgetSetting } from '@libs/types/src/services/types/service-settings-user-widgets.type';
import { DashboardPerformedWorkObject } from '@libs/types/src/services/object-type/dashboard/dashboard-performed-work.object';
import { TimePeriodEnum } from '@libs/types/src/common/enum/timePeriod.enum';
import { UIChart } from '@openng/optimus-ui/chart';
import { Chart, ChartData, ChartOptions, TooltipModel } from 'chart.js';
import { TranslateService } from '@ngx-translate/core';
import { DashboardChartWidgetStore } from '../dashboard-chart-widget.store';
import {
    dashboardChartTooltipPosition,
    dashboardPeriodDateKeys,
    dashboardServiceIds,
} from '../dashboard-chart-widget.utils';
import { dashboardPerformedWorkFragment } from '../dashboard-chart-widget.query';
import { LoadingComponent } from '../../../../../../components/kit/loading/loading.component';
import { EmptyStateComponent } from '../../../../../shared/components/empty-state/empty-state.component';
import { SessionService } from '../../../../../../services/session.service';
import { DashboardChartTooltipComponent } from '../dashboard-chart-tooltip/dashboard-chart-tooltip.component';
import { DashboardChartTooltipData } from '../dashboard-chart-tooltip/dashboard-chart-tooltip.types';
import {
    DateScrollerInputComponent,
} from '../../../../../../components/kit/input/date-scroller-input-v2/date-scroller-input.component';
import {
    DateScrollerRange,
} from '../../../../../../components/kit/input/date-scroller-input-v2/date-scroller-input.types';

@Component({
    selector: 'app-performed-work-widget',
    templateUrl: './performed-work-widget.component.html',
    styleUrl: './performed-work-widget.component.scss',
    providers: [DashboardChartWidgetStore],
    imports: [
        FormField,
        DateScrollerInputComponent,
        DashboardChartTooltipComponent,
        UIChart,
        LoadingComponent,
        EmptyStateComponent,
    ],
})
export class PerformedWorkWidgetComponent {
    protected readonly store = inject(DashboardChartWidgetStore);
    private readonly translate = inject(TranslateService);
    private readonly session = inject(SessionService);
    private readonly zone = inject(NgZone);
    private readonly colors = {
        statutory: '#4b82db',
        informative: '#f28c3c',
        facultative: '#9b9b9b',
    } as const;

    public readonly items = input<{ serviceId: string; widget: UserWidgetSetting }[]>([]);

    protected readonly dateFormData = signal({ date: new Date() as Date | null });
    protected readonly dateForm = form(this.dateFormData);
    protected readonly period = signal<DateScrollerRange>(this.currentMonth());
    protected readonly loading = this.store.loadingFor('getDashboardPerformedWork');
    protected readonly rows = computed<DashboardPerformedWorkObject[]>(
        () => this.store.data.getDashboardPerformedWork?.() ?? [],
    );
    protected readonly dateKeys = computed(() => dashboardPeriodDateKeys(this.period()));
    protected readonly chartData = computed<ChartData<'line'>>(() => ({
        labels: this.dateKeys().map(date => this.dateLabel(date)),
        datasets: [
            this.lineDataset(
                'dashboard.performedWork.statutory',
                this.colors.statutory,
                this.dateKeys().map(date => this.dailyValue(date, 'statutoryCount')),
            ),
            this.lineDataset(
                'dashboard.performedWork.informative',
                this.colors.informative,
                this.dateKeys().map(date => this.dailyValue(date, 'informativeCount')),
            ),
            this.lineDataset(
                'dashboard.performedWork.facultative',
                this.colors.facultative,
                this.dateKeys().map(date => this.dailyValue(date, 'facultativeCount')),
            ),
        ],
    }));
    protected readonly chartTooltip = signal<DashboardChartTooltipData | null>(null);
    protected readonly chartOptions: ChartOptions<'line'> = {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { intersect: false, mode: 'index' },
        plugins: {
            legend: { position: 'bottom' },
            tooltip: {
                enabled: false,
                external: ({ chart, tooltip }) => this.updateChartTooltip(chart, tooltip),
            },
        },
        scales: {
            y: { beginAtZero: true },
        },
    };

    constructor() {
        effect(() => {
            const serviceIds = dashboardServiceIds(this.items());
            const period = this.period();
            if (!serviceIds.length) return;

            this.store.api.getDashboardPerformedWork({
                serviceIds,
                dateFrom: period.dateFrom,
                dateTo: period.dateTo,
            }, dashboardPerformedWorkFragment);
        });
    }

    private dailyValue(
        date: string,
        field: 'statutoryCount' | 'informativeCount' | 'facultativeCount',
    ): number {
        return this.rows()
            .filter(row => row.date === date)
            .reduce((total, row) => total + row[field], 0);
    }

    private updateChartTooltip(chart: Chart, tooltip: TooltipModel<'line'>): void {
        if (!tooltip.opacity) {
            this.hideChartTooltip();
            return;
        }

        const dataIndex = tooltip.dataPoints[0]?.dataIndex;
        if (dataIndex === undefined) {
            this.hideChartTooltip();
            return;
        }
        const date = this.dateKeys()[dataIndex];
        const rows = this.rows().filter(row => row.date === date);
        const hasData = rows.some(row => row.statutoryCount || row.informativeCount || row.facultativeCount);
        if (!hasData) {
            this.hideChartTooltip();
            return;
        }

        this.zone.run(() => this.chartTooltip.set({
            ...dashboardChartTooltipPosition(chart.width, chart.height, tooltip.caretX, tooltip.caretY),
            title: String(chart.data.labels?.[dataIndex] ?? ''),
            summary: [
                {
                    label: this.translate.instant('dashboard.performedWork.statutory'),
                    value: String(this.dailyValue(date, 'statutoryCount')),
                    color: this.colors.statutory,
                },
                {
                    label: this.translate.instant('dashboard.performedWork.informative'),
                    value: String(this.dailyValue(date, 'informativeCount')),
                    color: this.colors.informative,
                },
                {
                    label: this.translate.instant('dashboard.performedWork.facultative'),
                    value: String(this.dailyValue(date, 'facultativeCount')),
                    color: this.colors.facultative,
                },
            ],
            table: {
                columns: [
                    { label: this.translate.instant('dashboard.performedWork.tooltipWorker') },
                    { label: this.translate.instant('dashboard.performedWork.tooltipStatutory'), align: 'right' },
                    { label: this.translate.instant('dashboard.performedWork.tooltipInformative'), align: 'right' },
                    { label: this.translate.instant('dashboard.performedWork.tooltipFacultative'), align: 'right' },
                ],
                rows: rows.map(row => [
                    row.worker,
                    String(row.statutoryCount),
                    String(row.informativeCount),
                    String(row.facultativeCount),
                ]),
            },
        }));
    }

    private hideChartTooltip(): void {
        this.zone.run(() => this.chartTooltip.set(null));
    }

    private lineDataset(label: string, borderColor: string, data: number[]) {
        return {
            label: this.translate.instant(label),
            data,
            borderColor,
            backgroundColor: borderColor,
            tension: 0.2,
        };
    }

    private dateLabel(value: string): string {
        return new Intl.DateTimeFormat(this.session.locale, { day: 'numeric', month: 'numeric' })
            .format(new Date(`${value}T12:00:00`));
    }

    private currentMonth(): DateScrollerRange {
        const dateFrom = new Date();
        dateFrom.setHours(0, 0, 0, 0);
        dateFrom.setDate(1);
        const dateTo = new Date(dateFrom.getFullYear(), dateFrom.getMonth() + 1, 0, 23, 59, 59, 999);
        return { dateFrom, dateTo, type: TimePeriodEnum.Month, length: 1 };
    }

    protected readonly TimePeriodEnum = TimePeriodEnum;
}
